import React, { useState, useEffect } from 'react'
import { ChevronRight, Edit, Menu } from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'
import Breadcrumb from '../../components/Common/Breadcrumb'
import HelpModal from '../../components/HelpModal/HelpModal'
import menuItemService from '../../services/menuItemService'
import { toast } from 'react-toastify'

const MenuItemMasterView = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const menuItemData = location.state?.menuItemData || {}
  const id = menuItemData.id || menuItemData._id

  const [data, setData] = useState({
    menuCode: '',
    label: '',
    path: '',
    icon: '',
    parent: null,
    sortOrder: 0,
    isActive: true,
    description: ''
  })
  const [loading, setLoading] = useState(!!id)
  const [showHelp, setShowHelp] = useState(false)

  useEffect(() => {
    const loadItem = async () => {
      if (!id) {
        setLoading(false)
        if (menuItemData.menuCode) {
          setData({
            menuCode: menuItemData.menuCode,
            label: menuItemData.label,
            path: menuItemData.path,
            icon: menuItemData.icon,
            parent: menuItemData.parent,
            sortOrder: menuItemData.sortOrder ?? 0,
            isActive: menuItemData.isActive !== false,
            description: menuItemData.description || ''
          })
        }
        return
      }
      try {
        setLoading(true)
        const res = await menuItemService.getMenuItemById(id)
        if (res?.success && res.data) {
          const d = res.data
          setData({
            menuCode: d.menuCode || '',
            label: d.label || '',
            path: d.path || '',
            icon: d.icon || '',
            parent: d.parent || null,
            sortOrder: d.sortOrder ?? 0,
            isActive: d.isActive !== false,
            description: d.description || ''
          })
        }
      } catch (err) {
        toast.error('Failed to load menu item')
        navigate('/settings/menu-item-master')
      } finally {
        setLoading(false)
      }
    }
    loadItem()
  }, [id])

  const breadcrumbItems = [
    { label: 'Settings', path: '/settings', onClick: () => navigate('/settings') },
    { label: 'Masters', path: '/settings', onClick: () => navigate('/settings') },
    { label: 'Menu Item Master', path: '/settings/menu-item-master', onClick: () => navigate('/settings/menu-item-master') },
    { label: 'View Menu Item', path: '', isCurrent: true }
  ]

  const helpContent = (
    <>
      <h3>View Menu Item</h3>
      <p>Read-only view of the menu item. Use Edit to make changes.</p>
    </>
  )

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="breadcrumb-section"><Breadcrumb items={breadcrumbItems} showBackButton={false} /></div>
          <div className="text-center py-5">Loading...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="page-container">
      <div className="page-content">
        <div className="breadcrumb-section">
          <Breadcrumb
            items={breadcrumbItems}
            showBackButton={false}
            rightSlot={
              <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
                <Menu size={18} />
                <span className="d-none d-sm-inline">Help</span>
              </button>
            }
          />
        </div>

        <div className="page-header">
          <div className="page-title">
            <h1>View Menu Item</h1>
            <p>{data.label || 'Menu item details'}</p>
          </div>
          <div className="page-actions">
            <button type="button" className="btn btn-primary" onClick={() => navigate('/settings/menu-item-master/edit', { state: { menuItemData: { ...data, id: id || menuItemData.id, _id: id || menuItemData._id } } })}>
              <Edit size={18} />
              Edit
            </button>
          </div>
        </div>

        <div className="card">
          <div className="form-header-dark">
            <h2>Menu Item Details</h2>
          </div>
          <div className="card-body">
            <div className="form-content view-mode">
              <div className="grid-row-4">
                <div className="form-group">
                  <label className="form-label">Menu Code</label>
                  <p className="form-value">{data.menuCode || '-'}</p>
                </div>
                <div className="form-group">
                  <label className="form-label">Label</label>
                  <p className="form-value">{data.label || '-'}</p>
                </div>
                <div className="form-group">
                  <label className="form-label">Path</label>
                  <p className="form-value">{data.path || '-'}</p>
                </div>
                <div className="form-group">
                  <label className="form-label">Icon</label>
                  <p className="form-value">{data.icon || '-'}</p>
                </div>
                <div className="form-group">
                  <label className="form-label">Parent</label>
                  <p className="form-value">{data.parent?.label || (data.parent && typeof data.parent === 'object' ? data.parent.label : '') || 'None'}</p>
                </div>
                <div className="form-group">
                  <label className="form-label">Sort Order</label>
                  <p className="form-value">{data.sortOrder ?? 0}</p>
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <p className="form-value"><span className={`status-badge ${data.isActive ? 'active' : 'inactive'}`}>{data.isActive ? 'Active' : 'Inactive'}</span></p>
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <p className="form-value">{data.description || '-'}</p>
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/settings/menu-item-master')}>
                  Back to List
                </button>
              </div>
            </div>
          </div>
        </div>

        <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="View Menu Item – Help">
          {helpContent}
        </HelpModal>
      </div>
    </div>
  )
}

export default MenuItemMasterView
